import React, { useState } from 'react';
import { 
  Store, User, Phone, MapPin, 
  ReceiptText, IndianRupee, CreditCard, 
  UserCheck, Sparkles 
} from 'lucide-react';

function PendingBills() {
  const [formData, setFormData] = useState({
    shopName: '',
    ownerName: '',
    whatsappNumber: '',
    address: '',
    pendingAmount: '',
    invoiceNumber: '',
    salesExecutiveName: '',
    contactNumber: '',
    paymentStatus: 'Pending',
  });

  // Added a loading state for better user experience
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true); // Disable button and show loading text

    try {
      // ✅ FIX: Corrected the fetch URL string and added /add to the end
      const response = await fetch('https://api-dg3-core-inv5.adminpaymentdailygo.co.in/api/pending-bills/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert('🎉 Bill saved successfully!');
        
        // Clear the form back to default after a successful save
        setFormData({
          shopName: '',
          ownerName: '',
          whatsappNumber: '',
          address: '',
          pendingAmount: '',
          invoiceNumber: '',
          salesExecutiveName: '',
          contactNumber: '',
          paymentStatus: 'Pending',
        });
      } else {
        alert(`❌ Error: ${data.message || 'Failed to save bill'}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('❌ Server error. Please check if your backend is running.');
    } finally {
      setIsLoading(false); // Re-enable the button
    }
  };

  return (
    // Ambient Background with subtle radial glows
    <div className="min-h-screen bg-[#0B0F19] relative flex items-center justify-center p-6 lg:p-8 overflow-hidden z-0">
      
      {/* Background Glowing Orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-600/20 rounded-full blur-[120px] -z-10 pointer-events-none"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-[120px] -z-10 pointer-events-none"></div>

      <div className="max-w-4xl w-full">
        
        {/* Header */}
        <div className="mb-8 text-center md:text-left">
          <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white via-indigo-100 to-slate-400 tracking-tight flex items-center justify-center md:justify-start gap-3">
            Add Pending Bill
            <Sparkles className="text-indigo-400" size={24} />
          </h1>
          <p className="text-slate-400 text-sm mt-2 font-medium">Log a new outstanding payment into the ledger.</p>
        </div>

        {/* Form Card - Glassmorphism Effect */}
        <div className="bg-[#111624]/80 backdrop-blur-2xl border border-white/5 rounded-3xl shadow-[0_0_40px_-15px_rgba(79,70,229,0.15)] p-6 lg:p-10 relative overflow-hidden">
          
          {/* Subtle top border highlight */}
          <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent"></div>

          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* --- SECTION: Shop Details --- */}
            <div>
              <h3 className="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-4 border-b border-white/5 pb-2">
                Shop Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                
                {/* Shop Name */}
                <div className="group">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1">Shop Name</label>
                  <div className="flex items-center bg-[#0B0F19]/50 border border-white/5 rounded-xl px-3 py-2.5 transition-all focus-within:border-indigo-500 focus-within:bg-[#0B0F19] focus-within:shadow-[0_0_15px_rgba(79,70,229,0.15)] hover:border-white/10">
                    <Store className="text-slate-500 group-focus-within:text-indigo-400 mr-3 transition-colors" size={18} />
                    <input type="text" name="shopName" value={formData.shopName} onChange={handleChange} placeholder="Supermart Direct" className="w-full bg-transparent text-white placeholder-slate-600 outline-none text-sm" required />
                  </div>
                </div>

                {/* Owner Name */}
                <div className="group">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1">Owner Name</label>
                  <div className="flex items-center bg-[#0B0F19]/50 border border-white/5 rounded-xl px-3 py-2.5 transition-all focus-within:border-indigo-500 focus-within:bg-[#0B0F19] focus-within:shadow-[0_0_15px_rgba(79,70,229,0.15)] hover:border-white/10">
                    <User className="text-slate-500 group-focus-within:text-indigo-400 mr-3 transition-colors" size={18} />
                    <input type="text" name="ownerName" value={formData.ownerName} onChange={handleChange} placeholder="John Doe" className="w-full bg-transparent text-white placeholder-slate-600 outline-none text-sm" required />
                  </div>
                </div>

                {/* WhatsApp Number */}
                <div className="group">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1">Shop WhatsApp</label>
                  <div className="flex items-center bg-[#0B0F19]/50 border border-white/5 rounded-xl px-3 py-2.5 transition-all focus-within:border-indigo-500 focus-within:bg-[#0B0F19] focus-within:shadow-[0_0_15px_rgba(79,70,229,0.15)] hover:border-white/10">
                    <Phone className="text-slate-500 group-focus-within:text-indigo-400 mr-3 transition-colors" size={18} />
                    <input type="tel" name="whatsappNumber" value={formData.whatsappNumber} onChange={handleChange} placeholder="+91 98765 43210" className="w-full bg-transparent text-white placeholder-slate-600 outline-none text-sm" required />
                  </div>
                </div>

                {/* Address */}
                <div className="group md:col-span-2">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1">Address</label>
                  <div className="flex items-start bg-[#0B0F19]/50 border border-white/5 rounded-xl px-3 py-2.5 transition-all focus-within:border-indigo-500 focus-within:bg-[#0B0F19] focus-within:shadow-[0_0_15px_rgba(79,70,229,0.15)] hover:border-white/10">
                    <MapPin className="text-slate-500 group-focus-within:text-indigo-400 mr-3 mt-0.5 transition-colors" size={18} />
                    <textarea name="address" value={formData.address} onChange={handleChange} rows="2" placeholder="123 Market Street, City..." className="w-full bg-transparent text-white placeholder-slate-600 outline-none text-sm resize-none" required></textarea>
                  </div>
                </div>
              </div>
            </div>

            {/* --- SECTION: Billing Details --- */}
            <div>
              <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-4 border-b border-white/5 pb-2">
                Billing Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                
                {/* Invoice Number */}
                <div className="group">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1">Invoice Number</label>
                  <div className="flex items-center bg-[#0B0F19]/50 border border-white/5 rounded-xl px-3 py-2.5 transition-all focus-within:border-emerald-500 focus-within:bg-[#0B0F19] focus-within:shadow-[0_0_15px_rgba(16,185,129,0.1)] hover:border-white/10">
                    <ReceiptText className="text-slate-500 group-focus-within:text-emerald-400 mr-3 transition-colors" size={18} />
                    <input type="text" name="invoiceNumber" value={formData.invoiceNumber} onChange={handleChange} placeholder="INV-2026-001" className="w-full bg-transparent text-white placeholder-slate-600 outline-none text-sm uppercase" required />
                  </div>
                </div>

                {/* Pending Amount */}
                <div className="group">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1">Pending Amount (₹)</label>
                  <div className="flex items-center bg-[#0B0F19]/50 border border-white/5 rounded-xl px-3 py-2.5 transition-all focus-within:border-emerald-500 focus-within:bg-[#0B0F19] focus-within:shadow-[0_0_15px_rgba(16,185,129,0.1)] hover:border-white/10">
                    <IndianRupee className="text-slate-500 group-focus-within:text-emerald-400 mr-3 transition-colors" size={18} />
                    <input type="number" name="pendingAmount" value={formData.pendingAmount} onChange={handleChange} placeholder="5000" className="w-full bg-transparent text-white placeholder-slate-600 outline-none text-sm font-semibold tracking-wide" required />
                  </div>
                </div>

                {/* Payment Status Dropdown */}
                <div className="group">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1">Payment Status</label>
                  <div className="flex items-center bg-[#0B0F19]/50 border border-white/5 rounded-xl px-3 py-2.5 transition-all focus-within:border-emerald-500 focus-within:bg-[#0B0F19] focus-within:shadow-[0_0_15px_rgba(16,185,129,0.1)] hover:border-white/10 relative">
                    <CreditCard className="text-slate-500 group-focus-within:text-emerald-400 mr-3 transition-colors" size={18} />
                    <select name="paymentStatus" value={formData.paymentStatus} onChange={handleChange} className="w-full bg-transparent text-white outline-none text-sm appearance-none cursor-pointer">
                      <option value="Pending" className="bg-[#111624] text-white">Pending</option>
                      <option value="Partially Paid" className="bg-[#111624] text-white">Partially Paid</option>
                      <option value="Overdue" className="bg-[#111624] text-red-400">Overdue</option>
                      <option value="Completed" className="bg-[#111624] text-emerald-400">Completed</option>
                    </select>
                    {/* Custom Dropdown Arrow */}
                    <div className="absolute right-4 pointer-events-none">
                      <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* --- SECTION: Executive Details --- */}
            <div>
              <h3 className="text-xs font-bold text-purple-400 uppercase tracking-wider mb-4 border-b border-white/5 pb-2">
                Executive Assignment
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Sales Executive Name */}
                <div className="group">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1">Sales Executive</label>
                  <div className="flex items-center bg-[#0B0F19]/50 border border-white/5 rounded-xl px-3 py-2.5 transition-all focus-within:border-purple-500 focus-within:bg-[#0B0F19] focus-within:shadow-[0_0_15px_rgba(168,85,247,0.1)] hover:border-white/10">
                    <UserCheck className="text-slate-500 group-focus-within:text-purple-400 mr-3 transition-colors" size={18} />
                    <input type="text" name="salesExecutiveName" value={formData.salesExecutiveName} onChange={handleChange} placeholder="Jane Smith" className="w-full bg-transparent text-white placeholder-slate-600 outline-none text-sm" />
                  </div>
                </div>

                {/* Exec Number */}
                <div className="group">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1">Executive Number</label>
                  <div className="flex items-center bg-[#0B0F19]/50 border border-white/5 rounded-xl px-3 py-2.5 transition-all focus-within:border-purple-500 focus-within:bg-[#0B0F19] focus-within:shadow-[0_0_15px_rgba(168,85,247,0.1)] hover:border-white/10">
                    <Phone className="text-slate-500 group-focus-within:text-purple-400 mr-3 transition-colors" size={18} />
                    <input type="tel" name="contactNumber" value={formData.contactNumber} onChange={handleChange} placeholder="+91 87654 32109" className="w-full bg-transparent text-white placeholder-slate-600 outline-none text-sm" />
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button Area */}
            <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between">
              <span className="text-xs text-slate-500 hidden md:block">* Please verify all details before submitting</span>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full md:w-auto px-8 py-3 font-semibold rounded-xl shadow-[0_0_20px_rgba(89,82,255,0.4)] transition-all flex items-center justify-center gap-2 
                  ${isLoading 
                    ? 'bg-indigo-400 cursor-not-allowed text-indigo-100' 
                    : 'bg-gradient-to-r from-indigo-600 to-[#5952FF] hover:from-indigo-500 hover:to-[#4a44d4] text-white transform hover:-translate-y-0.5 focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#111624] focus:ring-indigo-500'}`}
              >
                {isLoading ? 'Saving...' : 'Save Record'}
                {!isLoading && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}

export default PendingBills;